import React, { useEffect, useState } from "react";
import { Check, Loader2, Send, UserRound, X } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { RankEmblem } from "../rank/RankEmblem";
import { RankBar, Stat, labelOf } from "../rank/rankParts";
import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../lib/api";
import { CALIBRATION_GAMES, rankInfo } from "../../lib/ranks";
import { t, useLang } from "../../lib/i18n";
import { useSendInvite } from "./invites";

// The dot of a player's presence (backend/social.py): online, at a game, or away.
const DOT = { online: "bg-emerald-400", in_game: "bg-amber-400", offline: "bg-slate-500" };
const STATUS_TEXT = { online: "text-emerald-300", in_game: "text-amber-300", offline: "text-slate-400" };

export function PresenceDot({ status, className = "" }) {
  return <span className={`block rounded-full border-2 border-slate-900 ${DOT[status] || DOT.offline} ${className}`} />;
}

/**
 * Another player's card (GET /api/players/{id}): their picture and name, whether they are online,
 * their rank and record, and « Inviter à jouer » (POST /api/invites: a seat at the sender's friends'
 * table, which the sender then opens). Opened from the leaderboard, the « Récents » list, the
 * players' pills at the table and the waiting room's seats. `inviteBlocked`: why no invitation can
 * be sent from here (in a game, already at the same table).
 */
export function PlayerCardDialog({ userId, onClose, inviteBlocked }) {
  useLang();
  const { user } = useAuth();
  const [player, setPlayer] = useState(null);
  const [missing, setMissing] = useState(false);
  const { send, status } = useSendInvite();
  const state = status(userId);
  const sending = state === "sending";
  const sent = state === "sent";
  const error = sending || sent ? "" : state || "";

  useEffect(() => {
    if (!userId) return;
    setPlayer(null);
    setMissing(false);
    api.get(`/players/${userId}`).then(({ data }) => setPlayer(data.player)).catch(() => setMissing(true));
  }, [userId]);

  // From the Lobby a new table is opened for the invitation: the sender goes and waits there.
  const invite = () => send(userId, { onLeave: onClose });

  const info = rankInfo(player?.rank);
  const r = player?.rank || {};
  const self = userId === user?.user_id;

  return (
    <Dialog open={!!userId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        aria-describedby={undefined}
        data-testid="player-card"
        className="max-h-[94dvh] w-[calc(100vw-1.5rem)] max-w-sm gap-0 overflow-y-auto overflow-x-hidden rounded-2xl border-amber-500/20 bg-slate-900 p-0 text-white shadow-2xl shadow-black/60 sm:rounded-2xl [&>button]:hidden"
      >
        <div className="flex items-center gap-2 px-4 pt-4">
          <UserRound className="h-4 w-4 shrink-0 text-amber-300" />
          <DialogTitle className="text-sm font-semibold leading-none text-amber-200">{t("players.title")}</DialogTitle>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("profile.close")}
            className="-me-1.5 ms-auto rounded-full p-1.5 text-slate-500 transition-colors hover:bg-white/10 hover:text-white"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {missing ? (
          <p className="px-4 py-10 text-center text-sm text-slate-400">{t("players.notFound")}</p>
        ) : !player ? (
          <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-amber-300" /></div>
        ) : (
          <div className="px-4 pb-4 pt-3">
            <div className="flex items-center gap-3">
              <span className="relative shrink-0">
                <Avatar className="h-16 w-16 ring-2 ring-amber-400/50 ring-offset-2 ring-offset-slate-900">
                  <AvatarImage src={player.picture} />
                  <AvatarFallback className="bg-emerald-900 text-2xl text-amber-300">{player.name?.[0]?.toUpperCase()}</AvatarFallback>
                </Avatar>
                <PresenceDot status={player.status} className="absolute bottom-0 end-0 h-4 w-4" />
              </span>
              <div className="min-w-0">
                <div className="truncate text-lg text-white" dir="auto" data-testid="player-card-name">{player.name}</div>
                <div className="flex items-center gap-2 text-xs">
                  <span className={STATUS_TEXT[player.status]} data-testid="player-card-status">{t(`players.status.${player.status}`)}</span>
                  {player.is_guest && <span className="rounded-full bg-white/10 px-1.5 py-0.5 text-[10px] text-slate-300">{t("players.guest")}</span>}
                </div>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-3 rounded-2xl border border-white/5 bg-slate-950/50 px-3 py-2.5">
              <RankEmblem info={info} size={48} title={labelOf(info)} />
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="truncate font-display text-base italic text-amber-200">{labelOf(info)}</span>
                  <span className="shrink-0 text-[11px] tabular-nums text-slate-400">
                    {info.calibrating
                      ? t("rank.calibrationCount", { n: info.games, total: CALIBRATION_GAMES })
                      : t("rank.pr", { n: info.rp })}
                  </span>
                </div>
                <RankBar info={info.calibrating ? { progress: info.games / CALIBRATION_GAMES } : info} className="mt-1.5" />
              </div>
            </div>

            <div className="mt-3 grid grid-cols-3 gap-2" data-testid="player-card-stats">
              <Stat label={t("rank.stats.games")} value={r.games ?? 0} />
              <Stat label={t("rank.stats.wins")} value={r.wins ?? 0} accent="text-emerald-300" />
              <Stat label={t("rank.stats.losses")} value={r.losses ?? 0} accent="text-rose-300" />
              <Stat label={t("rank.stats.winRate")} value={r.games ? `${Math.round((100 * r.wins) / r.games)} %` : "—"} />
              <Stat label={t("rank.stats.bestStreak")} value={r.best_streak ?? 0} />
              <Stat label={t("rank.stats.roundsWon")} value={r.rounds_won ?? 0} />
            </div>

            {self ? (
              <p className="mt-4 text-center text-xs text-slate-400">{t("players.you")}</p>
            ) : (
              <div className="mt-4">
                <button
                  type="button"
                  onClick={invite}
                  disabled={!!inviteBlocked || sending || sent}
                  data-testid="player-card-invite"
                  className={`flex h-11 w-full items-center justify-center gap-2 rounded-xl text-sm transition-colors disabled:cursor-not-allowed ${
                    sent ? "bg-emerald-600/20 text-emerald-300" : "bg-amber-400 text-slate-900 hover:bg-amber-300 disabled:opacity-40"
                  }`}
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : sent ? <Check className="h-4 w-4" /> : <Send className="h-4 w-4 rtl:-scale-x-100" />}
                  {sent ? t("players.invited") : t("players.invite")}
                </button>
                {inviteBlocked ? (
                  <p className="mt-2 text-center text-xs text-slate-400">{inviteBlocked}</p>
                ) : error ? (
                  <p className="mt-2 text-center text-xs text-rose-300" role="alert" data-testid="player-card-error">{error}</p>
                ) : player.status !== "online" && (
                  <p className="mt-2 text-center text-xs text-slate-400">{t("players.waitNote")}</p>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default PlayerCardDialog;
